/* eslint-disable no-unused-vars */
import React, { useMemo, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useReminders } from "../context/ReminderContext";
import { Sun, Moon, Menu, Bell } from "lucide-react";
import NotificationDropdown from "./NotificationDropdown";
import "./topbar.css";

const PAGE_TITLES = {
  "/dashboard":             "Dashboard",
  "/dashboard/leads":       "Leads",
  "/dashboard/tasks":       "Tasks & Notes",
  "/dashboard/my-followups":"My Follow-ups",
  "/dashboard/my-customers":"My Customers",
  "/dashboard/my-team":     "My Team",
  "/dashboard/stock-list":  "Car Stock",
  "/dashboard/add-car":     "Add New Car",
  "/dashboard/customers":   "Customers & Orders",
  "/dashboard/customer-center": "Customer Center",
  "/dashboard/control-panel":   "Control Panel",
  "/dashboard/admin/customers": "All Customers",
  "/dashboard/commissions":     "Commission Settings",
  "/dashboard/salary-summary":  "Salary Summary",
  "/dashboard/profile":         "My Profile",
  "/dashboard/sales":           "Sales Tracking",
  "/dashboard/reports":         "Reports",
  "/dashboard/email":           "Email Center",
  "/dashboard/payroll":         "Payroll",
  "/dashboard/commission-settings": "Commission Settings",
  "/dashboard/attendance":      "Attendance",
  "/dashboard/attendance-history": "Attendance History",
  "/dashboard/salary":          "Salary",
  "/dashboard/team-portfolio":  "Team Portfolio",
  "/dashboard/auction":         "Auction (JPCenter)",
};

function useClock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 60000);
    return () => clearInterval(t);
  }, []);
  return time;
}

const Topbar = ({ onToggleMobileSidebar }) => {
  const { user: auth, userStatus, setUserStatus } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { dueCount, activeReminders } = useReminders();
  const [notifOpen, setNotifOpen] = useState(false);
  const user = auth?.user || auth;
  const location = useLocation();
  const clock = useClock();

  const pageTitle = useMemo(() => {
    return PAGE_TITLES[location.pathname] || "PAJ CRM";
  }, [location.pathname]);

  const formattedTime = useMemo(() => {
    return clock.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) +
      " · " + clock.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
  }, [clock]);

  return (
    <nav className="crm-topbar navbar">
      <div className="container-fluid crm-topbar__inner px-3 px-md-4">
        {/* Left */}
        <div className="crm-topbar-left">
          <button
            type="button"
            className="btn btn-sm text-reset p-1 me-2 d-md-none border-0"
            onClick={onToggleMobileSidebar}
            aria-label="Toggle Navigation"
            style={{ color: 'var(--text)' }}
          >
            <Menu size={22} />
          </button>
          <h1 className="crm-topbar-page mb-0">{pageTitle}</h1>
          <div className="crm-topbar-pill">Live</div>
        </div>

        {/* Right */}
        <div className="crm-topbar-right" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div className="crm-topbar-time d-none d-md-block">{formattedTime}</div>

          {/* Notification Bell Button */}
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              className="topbar-bell-btn"
              title="Lead Reminders & Alerts"
              onClick={() => setNotifOpen(prev => !prev)}
            >
              <Bell size={16} />
              {dueCount > 0 && (
                <span className="topbar-bell-count">{dueCount}</span>
              )}
              {dueCount === 0 && activeReminders.length > 0 && (
                <span className="topbar-bell-dot" />
              )}
            </button>

            <NotificationDropdown
              isOpen={notifOpen}
              onClose={() => setNotifOpen(false)}
            />
          </div>

          {/* Theme Toggle Button */}
          <button
            type="button"
            className="theme-toggle-btn"
            title={`Switch to ${theme === 'dark' ? 'Light' : 'Dark'} Mode`}
            onClick={toggleTheme}
            style={{
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              color: 'var(--text)',
              borderRadius: '8px',
              padding: '5px 10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            {theme === 'dark' ? <Sun size={14} style={{ color: '#f59e0b' }} /> : <Moon size={14} style={{ color: '#3b82f6' }} />}
            <span className="d-none d-sm-inline">{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
          </button>

          {auth?.role !== "Admin" && (<div className="crm-topbar-user" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              backgroundColor: userStatus === 'online' ? 'var(--success)' : userStatus === 'break' ? 'var(--warning)' : 'var(--primary)',
              boxShadow: userStatus === 'online' ? '0 0 6px var(--success)' : 'none',
              flexShrink: 0
            }} />
            <select 
              className="form-select form-select-sm border-0 bg-transparent fw-semibold p-0"
              style={{ width: 'auto', outline: 'none', boxShadow: 'none', cursor: 'pointer', color: 'var(--text)', fontSize: '12.5px' }}
              value={userStatus}
              onChange={(e) => setUserStatus(e.target.value)}
            >
              <option value="online">Online</option>
              <option value="break">On Break</option>
              <option value="meeting">In Meeting</option>
            </select>
          </div>)}
        </div>
      </div>
    </nav>
  );
};

export default Topbar;